import { Link } from 'react-router-dom';
import {
  BriefcaseIcon,
  Facebook,
  HomeIcon,
  Instagram,
  Linkedin,
  Lock,
  Twitter,
  WifiArcs,
} from './icons.jsx';
import { BRAND_COPY } from '../lib/questions.js';
import { CONTACT_DETAILS } from '../lib/siteContent.js';

/**
 * The lockup's arc takes the brand accent, matching the Figma: orange over the
 * purple home header, purple over the near-black business one. Both files carry
 * a white wordmark, so both read on their own ground.
 *
 * `logo-white.svg` is the all-white variant *with* the "For Home | For Business"
 * strapline — a different lockup, not a colour variant, so it is not used here.
 *
 * The footer is ink in both brands and the Figma gives it the orange arc either
 * way, so it always takes the home mark.
 */
export const LOGOS = {
  home: '/brand/logo-home.svg',
  business: '/brand/logo-business.svg',
};
const FOOTER_LOGO = LOGOS.home;

const SOCIALS = [
  { label: 'Facebook', href: 'https://facebook.com/', Icon: Facebook },
  { label: 'Twitter', href: 'https://twitter.com/', Icon: Twitter },
  { label: 'Instagram', href: 'https://instagram.com/', Icon: Instagram },
  { label: 'LinkedIn', href: 'https://linkedin.com/', Icon: Linkedin },
];

const FOOTER_LINKS = [
  { label: 'Home Broadband', href: '/' },
  { label: 'Business Broadband', href: '/business' },
  { label: 'Contact Us', href: '/contact' },
  { label: 'Privacy Policy', href: '/privacy' },
  { label: 'Cookies Policy', href: '/cookies' },
];

/** Where the two homepage variants live. `business` is a journey slug. */
export const HOME_ROUTES = { home: '/', business: '/for-business' };

/**
 * The marketing nav, shared by the homepage and the short-hero pages. `active`
 * marks which of the two audiences the current page belongs to; the legal and
 * contact pages belong to neither, so they pass nothing.
 */
export function SiteNav({ brand = 'home', active }) {
  return (
    <nav className="site-nav" aria-label="Main">
      <Link className="site-nav__logo" to="/" aria-label="SaveOnBroadband home">
        <img src={LOGOS[brand] ?? LOGOS.home} alt="SaveOnBroadband" width="111" height="51" />
      </Link>

      <ul className="site-nav__links">
        <li>
          <Link to={HOME_ROUTES.home} aria-current={active === 'home' ? 'page' : undefined}>
            For Home
          </Link>
        </li>
        <li>
          <Link to={HOME_ROUTES.business} aria-current={active === 'business' ? 'page' : undefined}>
            For Business
          </Link>
        </li>
      </ul>

      <Link className="site-nav__contact" to="/contact">
        Contact Us
      </Link>
    </nav>
  );
}

/**
 * The short hero the contact and legal pages sit under — a 319px band with the
 * nav, a title and one line of copy, and the signal fan bleeding off the right.
 */
export function PageHero({ brand = 'home', title, subtitle }) {
  return (
    <div className="page-hero">
      <WifiArcs className="page-hero__arcs" />
      <div className="page-hero__inner">
        <SiteNav brand={brand} />
        <div className="page-hero__content">
          <h1 className="page-hero__title">{title}</h1>
          {subtitle && <p className="page-hero__subtitle">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
}

/**
 * The purple band across the top. The swish is a decorative gradient stroke
 * bleeding off the top-right corner; it is clipped by the band rather than the
 * page so it cannot add horizontal scroll on narrow screens.
 */
export function Header({ brand = 'home' }) {
  const copy = BRAND_COPY[brand] ?? BRAND_COPY.home;
  const BrandIcon = brand === 'business' ? BriefcaseIcon : HomeIcon;

  return (
    <header className="site-header">
      <div className="site-header__swish" aria-hidden="true" />
      <div className="site-header__inner">
        <a className="site-header__logo" href="/" aria-label="SaveOnBroadband home">
          <img src={LOGOS[brand] ?? LOGOS.home} alt="SaveOnBroadband" width="111" height="51" />
        </a>
        <p className="site-header__tagline">
          <BrandIcon size={26} />
          <span>{copy.tagline}</span>
        </p>
      </div>
    </header>
  );
}

/**
 * The white band between the journey and the footer. Its button is the
 * opposite brand colour to the primary action above it — that inversion is
 * carried by --cta-callback, so it survives the brand swap.
 */
export function CallbackBand({
  brand = 'home',
  phone = CONTACT_DETAILS.phone,
  // The designer's 404 frame labels this button "Call us now" where every
  // other frame says "Request A Call Back", so the label is a prop rather than
  // a second component.
  label = 'Request A Call Back',
}) {
  const copy = BRAND_COPY[brand] ?? BRAND_COPY.home;

  return (
    <section className="callback">
      <div className="callback__inner">
        <p className="callback__text">{copy.callback}</p>
        <a className="callback__button" href={`tel:${phone.replace(/\s/g, '')}`}>
          {label}
        </a>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="site-footer">
      <div className="site-footer__inner">
        <div className="site-footer__main">
          <div className="site-footer__brand">
            <img src={FOOTER_LOGO} alt="SaveOnBroadband" width="185" height="85" />
            <p className="site-footer__blurb">
              Helping UK households find the best broadband deals with effortless, zero-downtime
              switching.
            </p>
            <ul className="site-footer__socials">
              {SOCIALS.map(({ label, href, Icon }) => (
                <li key={label}>
                  <a
                    className="site-footer__social"
                    href={href}
                    aria-label={label}
                    rel="noreferrer noopener"
                    target="_blank"
                  >
                    <Icon size={18} />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="site-footer__aside">
            <nav aria-label="Footer">
              <ul className="site-footer__links">
                {FOOTER_LINKS.map(({ label, href }) => (
                  <li key={label}>
                    <a href={href}>{label}</a>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="gdpr">
              <p className="gdpr__head">
                <span className="icon-well" aria-hidden="true">
                  <Lock size={16} />
                </span>
                GDPR Compliant
              </p>
              <p className="gdpr__body">We keep your data safe.</p>
            </div>
          </div>
        </div>

        <hr className="site-footer__rule" />

        <div className="site-footer__bottom">
          <p>© {new Date().getFullYear()} Save On Broadband. All rights reserved.</p>
          <p>Broadband Tailored for UK Households &amp; Businesses</p>
        </div>
      </div>
    </footer>
  );
}

export function Page({ brand = 'home', callbackLabel, children }) {
  return (
    <div className="page" data-brand={brand}>
      <Header brand={brand} />
      <main className="page__main">{children}</main>
      <CallbackBand brand={brand} label={callbackLabel} />
      <Footer />
    </div>
  );
}
